import 'tocbot/dist/tocbot.min.js';

const TOC_ROOT_SELECTOR = '.js-toc';
const CONTENT_SELECTOR = '.js-toc-content';
const LAYOUT_SELECTOR = '.js-toc-layout';
const HEADING_SELECTOR = 'h2, h3, h4';
const DESKTOP_MEDIA_QUERY = '(min-width: 1700px)';
const STICKY_TOP = 16;
const TOC_SCROLL_OFFSET = 120;
const TOC_EXCLUDE_CONTAINERS = [
  '.o-related-posts',
  '.glossary-related-learning',
  '.pre-footer-cta',
  '.sag-blog-single-prefooter',
];

function slugify(text) {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

function ensureHeadingIds(contentEl) {
  const used = new Set();
  const headings = contentEl.querySelectorAll(HEADING_SELECTOR);

  headings.forEach((heading) => {
    if (heading.id && heading.id.trim() !== '') {
      used.add(heading.id);
      return;
    }

    const label = (heading.textContent || '').trim();
    if (!label) {
      return;
    }

    const base = slugify(label) || 'section';
    let candidate = base;
    let i = 2;
    while (used.has(candidate) || document.getElementById(candidate)) {
      candidate = `${base}-${i}`;
      i += 1;
    }

    heading.id = candidate;
    used.add(candidate);
  });
}

function markExcludedHeadings(contentEl) {
  TOC_EXCLUDE_CONTAINERS.forEach((selector) => {
    const containers = contentEl.querySelectorAll(selector);
    containers.forEach((container) => {
      const headings = container.querySelectorAll(HEADING_SELECTOR);
      headings.forEach((heading) => heading.classList.add('js-toc-ignore'));
    });
  });
}

export default function Toc() {
  const tocbot = window.tocbot;
  if (!tocbot || typeof tocbot.init !== 'function') {
    return;
  }

  const tocRoots = document.querySelectorAll(TOC_ROOT_SELECTOR);
  if (!tocRoots.length) {
    return;
  }

  tocRoots.forEach((tocRoot) => {
    const container = tocRoot.closest(LAYOUT_SELECTOR);
    const contentEl = container ? container.querySelector(CONTENT_SELECTOR) : document.querySelector(CONTENT_SELECTOR);

    if (!contentEl) {
      return;
    }

    markExcludedHeadings(contentEl);
    ensureHeadingIds(contentEl);

    const headings = contentEl.querySelectorAll(HEADING_SELECTOR);
    if (headings.length < 3) {
      if (container) {
        container.classList.add('has-no-toc-items');
      }
      return;
    }

    const tocId = tocRoot.id || `toc-${Math.random().toString(36).slice(2, 9)}`;
    tocRoot.id = tocId;

    tocbot.init({
      tocSelector: `#${tocId}`,
      contentSelector: CONTENT_SELECTOR,
      headingSelector: HEADING_SELECTOR,
      ignoreSelector: '.js-toc-ignore',
      hasInnerContainers: true,
      collapseDepth: 0,
      scrollSmooth: false,
      headingsOffset: TOC_SCROLL_OFFSET,
      scrollSmoothOffset: -TOC_SCROLL_OFFSET,
      orderedList: false,
    });

    bindTocLinkScroll(tocRoot);
  });

  initSidecarSticky();
}

function bindTocLinkScroll(tocRoot) {
  if (!tocRoot || tocRoot.dataset.tocScrollBound === '1') {
    return;
  }

  let navigationTimeout;
  let navigationId = 0;
  let navigationScrollHandler;

  tocRoot.addEventListener('click', (event) => {
    const link = event.target.closest('a[href^="#"]');
    if (!link) {
      return;
    }

    const hash = link.getAttribute('href');
    if (!hash || hash === '#') {
      return;
    }

    const targetId = decodeURIComponent(hash.slice(1));
    const target = document.getElementById(targetId);
    if (!target) {
      return;
    }

    event.preventDefault();

    window.clearTimeout(navigationTimeout);
    if (navigationScrollHandler) {
      window.removeEventListener('scroll', navigationScrollHandler);
    }
    navigationId += 1;
    const currentNavigationId = navigationId;
    tocRoot.classList.add('is-toc-navigating');
    tocRoot.querySelectorAll('.is-clicked-link').forEach((clickedLink) => {
      clickedLink.classList.remove('is-clicked-link');
    });
    link.classList.add('is-clicked-link');

    const finishNavigation = () => {
      if (currentNavigationId !== navigationId) {
        return;
      }
      window.removeEventListener('scroll', navigationScrollHandler);
      navigationScrollHandler = null;
      tocRoot.classList.remove('is-toc-navigating');
      link.classList.remove('is-clicked-link');
    };

    navigationScrollHandler = () => {
      window.clearTimeout(navigationTimeout);
      navigationTimeout = window.setTimeout(finishNavigation, 180);
    };
    window.addEventListener('scroll', navigationScrollHandler, { passive: true });
    navigationTimeout = window.setTimeout(finishNavigation, 300);

    const top = Math.max(0, (target.getBoundingClientRect().top + window.scrollY) - TOC_SCROLL_OFFSET);
    window.history.pushState(null, '', `#${targetId}`);
    window.scrollTo({ top, behavior: 'smooth' });
  });

  tocRoot.dataset.tocScrollBound = '1';
}

function initSidecarSticky() {
  const media = window.matchMedia(DESKTOP_MEDIA_QUERY);
  const layouts = document.querySelectorAll(LAYOUT_SELECTOR);
  if (!layouts.length) {
    return;
  }

  const updateAll = () => {
    layouts.forEach((layout) => updateLayoutSticky(layout, media.matches));
  };

  updateAll();
  window.addEventListener('scroll', updateAll, { passive: true });
  window.addEventListener('resize', updateAll);
}

function updateLayoutSticky(layout, isDesktop) {
  const content = layout.querySelector(CONTENT_SELECTOR);
  const aside = layout.querySelector('.sag-content-toc-shell__aside');
  if (!content || !aside) {
    return;
  }

  aside.classList.remove('is-sticky-fixed', 'is-sticky-bottom');
  aside.classList.remove('is-sticky-top');
  aside.classList.remove('is-hidden-offrange');
  aside.style.removeProperty('left');
  aside.style.removeProperty('width');
  aside.style.removeProperty('top');
  aside.style.removeProperty('position');
  aside.style.removeProperty('margin-left');

  if (!isDesktop) {
    return;
  }

  const layoutRect = layout.getBoundingClientRect();
  const contentRect = content.getBoundingClientRect();
  const asideRect = aside.getBoundingClientRect();
  const asideHeight = asideRect.height;
  const threshold = STICKY_TOP;
  const contentTopDoc = contentRect.top + window.scrollY;
  const contentBottomDoc = getEffectiveContentBottomDoc(content);
  const scrollY = window.scrollY;
  const startStickY = contentTopDoc - threshold;
  const stopStickY = contentBottomDoc - asideHeight - threshold;
  const effectiveBottomRectTop = contentBottomDoc - window.scrollY;
  const isContentOnScreen = effectiveBottomRectTop > 0 && contentRect.top < window.innerHeight;

  if (!isContentOnScreen) {
    aside.classList.add('is-hidden-offrange');
    return;
  }

  // Before content reaches top threshold: keep at top of content.
  if (scrollY < startStickY) {
    aside.classList.add('is-sticky-top');
    aside.style.position = 'absolute';
    aside.style.top = '0px';
    return;
  }

  // After bottom is reached: pin to bottom of content bounds.
  if (scrollY >= stopStickY) {
    const contentOffsetTop = contentRect.top - layoutRect.top;
    const effectiveContentHeight = contentBottomDoc - contentTopDoc;
    const finalTop = contentOffsetTop + effectiveContentHeight - asideHeight;
    aside.classList.add('is-sticky-bottom');
    aside.style.position = 'absolute';
    aside.style.top = `${Math.max(0, finalTop)}px`;
    return;
  }

  aside.classList.add('is-sticky-fixed');
  aside.style.position = 'fixed';
  aside.style.top = `${threshold}px`;
  aside.style.left = `${asideRect.left}px`;
  aside.style.width = `${aside.offsetWidth}px`;
  aside.style.marginLeft = '0px';
}

function getEffectiveContentBottomDoc(content) {
  const contentRect = content.getBoundingClientRect();
  let boundaryDoc = contentRect.bottom + window.scrollY;

  TOC_EXCLUDE_CONTAINERS.forEach((selector) => {
    const node = content.querySelector(selector);
    if (!node) {
      return;
    }
    const rect = node.getBoundingClientRect();
    const topDoc = rect.top + window.scrollY;
    if (topDoc > 0 && topDoc < boundaryDoc) {
      boundaryDoc = topDoc;
    }
  });

  return boundaryDoc;
}
